import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarBlank, Clock, ForkKnife, MapPin, ArrowsClockwise, Warning, FileXls, FilePdf } from "@phosphor-icons/react";

/**
 * Heures & Déplacements — remplace l'ancien /admin/traces GPS.
 * Récupère : heures effectives (saisies iOS via /api/hours), paniers,
 * et km vol d'oiseau depuis le dépôt vers chaque chantier assigné.
 */
export default function HeuresDeplacementsPage() {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  // Lundi → Vendredi de la semaine en cours (jamais un range croisé
  // vendredi → jeudi comme "7 derniers jours" pouvait le faire).
  const [from, setFrom] = useState(() => {
    const d = new Date(today);
    const dow = d.getDay(); // 0 = Dim, 1 = Lun … 6 = Sam
    const diffToMon = dow === 0 ? -6 : 1 - dow;
    d.setDate(d.getDate() + diffToMon);
    return iso(d);
  });
  const [to, setTo] = useState(() => {
    const d = new Date(today);
    const dow = d.getDay();
    const diffToFri = dow === 0 ? -2 : 5 - dow;
    d.setDate(d.getDate() + diffToFri);
    return iso(d);
  });
  const [userId, setUserId] = useState("all");
  const [users, setUsers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.role !== "admin" && u.is_active !== false)));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from, to });
      if (userId !== "all") params.set("user_id", userId);
      const r = await api.get(`/hours-deplacements?${params}`);
      setData(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, userId]);

  const grouped = useMemo(() => {
    if (!data?.rows) return [];
    const map = new Map();
    for (const r of data.rows) {
      if (!map.has(r.user_id)) map.set(r.user_id, { user_id: r.user_id, name: r.user_name, color: r.user_color, role: r.user_role, rows: [] });
      map.get(r.user_id).rows.push(r);
    }
    return Array.from(map.values());
  }, [data]);

  const exportFile = async (format) => {
    try {
      const params = new URLSearchParams({ from, to });
      if (userId !== "all") params.set("user_id", userId);
      const r = await api.get(`/hours-deplacements/export.${format}?${params}`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: r.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `heures-deplacements_${from}_${to}.${format === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-[#005CFF]/15 to-[#005CFF]/5 border border-[#005CFF]/25 p-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#005CFF] flex items-center justify-center shadow-[0_0_20px_rgba(0,92,255,0.4)]">
          <Clock size={22} weight="fill" color="white" />
        </div>
        <div className="flex-1">
          <div className="font-display font-semibold">Heures &amp; Déplacements</div>
          <div className="text-xs text-white/60">
            Heures effectives (app iOS) · Paniers auto (lun→ven) · Zone URSSAF déterminée depuis le chantier le plus éloigné
          </div>
        </div>
        <Button
          onClick={() => exportFile("xlsx")}
          variant="ghost"
          className="border border-emerald-500/40 h-9 gap-1.5 text-emerald-300 hover:bg-emerald-500/10"
          data-testid="hd-export-xlsx"
          title="Export Excel (Audrey compta)"
        >
          <FileXls size={14} weight="fill" /> Excel
        </Button>
        <Button
          onClick={() => exportFile("pdf")}
          variant="ghost"
          className="border border-red-500/40 h-9 gap-1.5 text-red-300 hover:bg-red-500/10"
          data-testid="hd-export-pdf"
          title="Export PDF (Audrey compta)"
        >
          <FilePdf size={14} weight="fill" /> PDF
        </Button>
        <Button onClick={load} variant="ghost" disabled={loading} className="border border-white/10 h-9">
          <ArrowsClockwise size={13} className={loading ? "animate-spin" : ""} />
        </Button>
      </div>

      {/* Filters */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Du</div>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full h-10 rounded-xl bg-white/5 border border-white/10 px-3 text-white" data-testid="hd-from" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Au</div>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full h-10 rounded-xl bg-white/5 border border-white/10 px-3 text-white" data-testid="hd-to" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Technicien</div>
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger className="bg-white/5 border-white/10 h-10" data-testid="hd-user"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111] border-white/10 text-white max-h-64">
              <SelectItem value="all">Toute l&apos;équipe</SelectItem>
              {users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Totals */}
      {data?.totals && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Heures norm." value={`${data.totals.hours_normal.toFixed(1)}h`} icon={Clock} color="#7CB2FF" />
          <Stat label="Heures sup" value={`${data.totals.hours_extra.toFixed(1)}h`} icon={Clock} color="#F59E0B" />
          <Stat label="Paniers" value={data.totals.paniers} icon={ForkKnife} color="#10B981" />
          <ZoneStat zonesConfig={data.zones_config || []} zonesCounts={data.totals.zones_counts || {}} totalKm={data.totals.km_total} />
        </div>
      )}

      {/* Rows */}
      <div className="space-y-3">
        {loading && !data ? (
          <div className="h-32 rounded-2xl bg-white/5 animate-pulse" />
        ) : grouped.length === 0 ? (
          <div className="rounded-2xl bg-white/[0.02] border border-dashed border-white/10 p-8 text-center text-sm text-white/50">
            Aucune donnée sur la période sélectionnée.
          </div>
        ) : (
          grouped.map((g) => (
            <div key={g.user_id} className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden" data-testid={`hd-user-${g.user_id}`}>
              <div className="px-4 py-2.5 flex items-center gap-3 border-b border-white/5" style={{ background: `${g.color}0F` }}>
                <div className="w-8 h-8 rounded-lg" style={{ background: g.color }} />
                <div className="flex-1">
                  <div className="font-display font-semibold text-sm">{g.name}</div>
                  <div className="text-[10px] text-white/50 uppercase tracking-widest">{g.role}</div>
                </div>
                <div className="text-[11px] font-mono text-white/70">
                  {g.rows.reduce((a, r) => a + r.hours_normal + r.hours_extra, 0).toFixed(1)}h ·
                  {" "}{g.rows.filter((r) => r.panier).length} panier(s)
                </div>
              </div>
              <div className="divide-y divide-white/5">
                {g.rows.map((r) => (
                  <div key={r.date} className="grid grid-cols-6 gap-2 px-4 py-2 text-xs items-center" data-testid={`hd-row-${g.user_id}-${r.date}`}>
                    <div className="text-white/80">
                      <div>{new Date(r.date).toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" })}</div>
                      {r.source === "baseline" && (
                        <div className="text-[9px] text-amber-300/70 flex items-center gap-0.5"><Warning size={9} weight="fill" /> socle</div>
                      )}
                    </div>
                    <div className="text-white/80 font-mono tabular-nums">{r.hours_normal.toFixed(2)}h</div>
                    <div className="font-mono tabular-nums text-amber-300">{r.hours_extra > 0 ? `+${r.hours_extra.toFixed(2)}h` : "—"}</div>
                    <div className={r.panier ? "text-emerald-300" : "text-white/30"}>{r.panier ? "✓ Panier" : "—"}</div>
                    <div className="flex items-center gap-1.5" title={`${r.km_vol_oiseau.toFixed(1)} km vol d'oiseau depuis ${r.km_origin === "home" ? "le domicile" : "le dépôt"}`}>
                      {r.zone ? (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#A855F7]/15 text-[#D8B4FE] border border-[#A855F7]/30 font-semibold">
                          Zone {r.zone}
                        </span>
                      ) : (
                        <span className="text-white/30">—</span>
                      )}
                      {r.km_origin === "home" && (
                        <span className="text-[8px] uppercase tracking-widest px-1 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/25" title="Origine: domicile du tech">
                          Dom
                        </span>
                      )}
                    </div>
                    <div className="text-white/50 truncate">{r.chantier_name || (r.note ? r.note.slice(0, 30) : "")}</div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {data?.depot && (
        <div className="text-[10px] text-white/40 text-center flex items-center justify-center gap-1">
          <MapPin size={10} weight="fill" /> Dépôt de référence : {data.depot.name} ({data.depot.latitude.toFixed(4)}, {data.depot.longitude.toFixed(4)})
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3" style={{ borderColor: `${color}30` }}>
      <div className="flex items-center gap-1.5 text-[10px] text-white/50 uppercase tracking-widest">
        <Icon size={12} weight="duotone" style={{ color }} /> {label}
      </div>
      <div className="font-display text-2xl font-bold tabular-nums mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}

function ZoneStat({ zonesConfig, zonesCounts, totalKm }) {
  const color = "#A855F7";
  const orderedCodes = (zonesConfig || []).map((z) => z.code).filter(Boolean);
  const hasData = orderedCodes.some((c) => (zonesCounts[c] || 0) > 0);
  return (
    <div
      className="rounded-xl bg-white/[0.03] border border-white/10 p-3"
      style={{ borderColor: `${color}30` }}
      data-testid="hd-zones-stat"
      title={`Total km vol d'oiseau (référence) : ${(totalKm || 0).toFixed(1)} km`}
    >
      <div className="flex items-center gap-1.5 text-[10px] text-white/50 uppercase tracking-widest">
        <MapPin size={12} weight="duotone" style={{ color }} /> Zones paniers
      </div>
      {hasData ? (
        <div className="mt-1 flex flex-wrap gap-1">
          {orderedCodes.map((code) => {
            const n = zonesCounts[code] || 0;
            if (!n) return null;
            return (
              <span
                key={code}
                className="text-[11px] px-1.5 py-0.5 rounded-full bg-[#A855F7]/15 text-[#D8B4FE] border border-[#A855F7]/30 font-mono tabular-nums"
                data-testid={`hd-zone-total-${code}`}
              >
                {n}× <span className="uppercase">Z{code}</span>
              </span>
            );
          })}
        </div>
      ) : (
        <div className="font-display text-2xl font-bold mt-0.5" style={{ color }}>—</div>
      )}
    </div>
  );
}
